import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import "./Navbar.css";

const Navbar = () => {
  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Get user data from local storage
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("todoapp"));
    setUsername(userData?.user?.username || "Guest");
    setUserEmail(userData?.user?.email || "");
  }, []);

  // Logout function
  const logoutHandler = () => {
    localStorage.removeItem("todoapp");
    toast.success("Logout Successfully! See you soon 👋");
    navigate("/login");
    setIsMenuOpen(false);
    setShowProfileMenu(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!username || username === "Guest") return "?";
    return username
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Check if link is active
  const isActive = (path) => location.pathname === path;

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setShowProfileMenu(false);
  };

  // Toggle profile dropdown
  const toggleProfileMenu = (e) => {
    e.stopPropagation();
    setShowProfileMenu(!showProfileMenu);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowProfileMenu(false);
    };

    if (showProfileMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => document.removeEventListener("click", handleClickOutside);
  }, [showProfileMenu]);

  const navLinks = [
    { path: "/home", label: "Home", icon: "🏠" },
    { path: "/todolist", label: "My Tasks", icon: "✅" },
  ];

  return (
    <nav className={`modern-navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar-container">
        {/* Logo/Brand Section */}
        <div className="navbar-brand">
          <div className="brand-icon">📋</div>
          <div className="brand-text">
            <span className="brand-title">TaskMaster</span>
            <span className="brand-subtitle">Stay Organized</span>
          </div>
        </div>

        {/* Desktop Navigation Links */}
        <div className="navbar-links desktop-only">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${isActive(link.path) ? "active" : ""}`}
            >
              <span className="nav-icon">{link.icon}</span>
              <span className="nav-label">{link.label}</span>
              {isActive(link.path) && <span className="active-indicator"></span>}
            </Link>
          ))}
        </div>

        {/* Right Section: User Profile & Actions */}
        <div className="navbar-actions">
          {/* User Profile Dropdown */}
          <div className="profile-dropdown">
            <button
              className="profile-trigger"
              onClick={toggleProfileMenu}
              aria-label="User menu"
            >
              <div className="user-avatar">
                <span className="avatar-text">{getUserInitials()}</span>
                <span className="online-indicator"></span>
              </div>
              <div className="user-info desktop-only">
                <span className="user-name">{username}</span>
                {userEmail && <span className="user-email">{userEmail}</span>}
              </div>
              <span className={`dropdown-arrow ${showProfileMenu ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {/* Profile Dropdown Menu */}
            {showProfileMenu && (
              <div className="profile-menu">
                <div className="profile-menu-header">
                  <div className="user-avatar-large">
                    <span className="avatar-text-large">{getUserInitials()}</span>
                  </div>
                  <div className="user-details">
                    <h4 className="menu-user-name">{username}</h4>
                    {userEmail && <p className="menu-user-email">{userEmail}</p>}
                  </div>
                </div>

                <div className="profile-menu-divider"></div>

                <div className="profile-menu-items">
                  <button className="profile-menu-item" onClick={() => navigate("/home")}>
                    <span className="menu-item-icon">🏠</span>
                    <span className="menu-item-text">Home</span>
                  </button>
                  <button className="profile-menu-item" onClick={() => navigate("/todolist")}>
                    <span className="menu-item-icon">✅</span>
                    <span className="menu-item-text">My Tasks</span>
                  </button>
                </div>

                <div className="profile-menu-divider"></div>

                <button className="profile-menu-item logout-item" onClick={logoutHandler}>
                  <span className="menu-item-icon">🚪</span>
                  <span className="menu-item-text">Logout</span>
                </button>
              </div>
            )}
          </div>

          {/* Quick Logout Button (Desktop) */}
          <button className="logout-btn desktop-only" onClick={logoutHandler}>
            <span className="logout-icon">🚪</span>
            <span className="logout-text">Logout</span>
          </button>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle mobile-only"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${isMenuOpen ? "open" : ""}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-content">
          {/* Mobile User Info */}
          <div className="mobile-user-card">
            <div className="user-avatar-mobile">
              <span className="avatar-text">{getUserInitials()}</span>
              <span className="online-indicator"></span>
            </div>
            <div className="mobile-user-info">
              <h3 className="mobile-user-name">{username}</h3>
              {userEmail && <p className="mobile-user-email">{userEmail}</p>}
            </div>
          </div>

          {/* Mobile Navigation Links */}
          <div className="mobile-nav-links">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`mobile-nav-link ${isActive(link.path) ? "active" : ""}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="mobile-nav-icon">{link.icon}</span>
                <span className="mobile-nav-label">{link.label}</span>
                {isActive(link.path) && <span className="mobile-active-dot"></span>}
              </Link>
            ))}
          </div>

          {/* Mobile Logout Button */}
          <button className="mobile-logout-btn" onClick={logoutHandler}>
            <span className="mobile-logout-icon">🚪</span>
            <span className="mobile-logout-text">Logout</span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="mobile-overlay" onClick={toggleMenu}></div>}
    </nav>
  );
};

export default Navbar;